import React, { FC, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  TextField,
  IconButton,
  InputAdornment,
  FormHelperText,
  Typography,
  Divider,
  Link,
  Checkbox,
  FormControlLabel,
  Stack,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useAppDispatch } from '../../store/hooks';
import { login } from '../../store/authSlice';
import SocialIcons from '../../components/SocialIcons/SocialIcons';

interface HeroLoginFormProps {
  setType: (type: string) => void;
  onOpenModal?: () => void;
}

const HeroLoginForm: FC<HeroLoginFormProps> = ({ setType, onOpenModal }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((s) => !s);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, marginBottom: 14 }}>
        <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#111' }}>Login</h3>
        {/* <Typography
          onClick={() => { setType('1'); onOpenModal?.(); }}
          variant="body2"
          sx={{ cursor: 'pointer', color: '#059669', fontWeight: 500 }}
        >
          Create account
        </Typography> */}
      </div>

      <Formik
        initialValues={{ email: '', password: '', agreeTerms: false, submit: null as any }}
        validationSchema={Yup.object().shape({
          email: Yup.string().required('Email or phone required'),
          password: Yup.string().required('Password required').min(4, 'Min 4 chars'),
          agreeTerms: Yup.bool().oneOf([true], 'You must accept terms'),
        })}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          try {
            setStatus({ success: false });
            setSubmitting(true);
            const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            let email = values.email;
            if (!emailRegex.test(email)) {
              email = '+91' + email;
            }
            const loginResponse: any = await dispatch(login({ email, password: values.password, otp: '' } as any));
            if (loginResponse.type === 'auth/login/fulfilled') {
              navigate('/home', { replace: true });
            } else {
              setSubmitting(false);
              if (loginResponse.payload && 'message' in loginResponse.payload) {
                setErrors({ submit: loginResponse.payload.message });
              } else {
                setErrors({ submit: 'Login failed' });
              }
            }
          } catch (err: any) {
            setStatus({ success: false });
            setErrors({ submit: err?.message || 'Login failed' });
            setSubmitting(false);
          }
        }}
      >
        {({ errors, touched, values, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue }) => (
          <form noValidate onSubmit={handleSubmit}>
            <div style={{ marginBottom: 10 }}>
              <label style={labelStyle}>Email address or mobile</label>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter email / phone"
                name="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.email && errors.email)}
                sx={textFieldSx}
              />
              {touched.email && errors.email && (
                <FormHelperText error>{errors.email as string}</FormHelperText>
              )}
            </div>

            <div style={{ marginBottom: 6 }}>
              <label style={labelStyle}>Password</label>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.password && errors.password)}
                sx={textFieldSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowPassword} edge="end" size="small">
                        {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {touched.password && errors.password && (
                <FormHelperText error>{errors.password as string}</FormHelperText>
              )}
            </div>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography
                onClick={onOpenModal}
                variant="body2"
                sx={{ cursor: 'pointer', color: '#059669', fontWeight: 600 }}
              >
                Login with OTP
              </Typography>
              <Typography
                onClick={() => { setType('2'); onOpenModal?.(); }}
                variant="body2"
                sx={{ cursor: 'pointer', color: '#111', fontWeight: 600 }}
              >
                Forgot password?
              </Typography>
            </Stack>

            <FormControlLabel
              sx={{ mt: 0, mb: 1, alignItems: 'flex-start', ml: 0, mr: 0 }}
              control={
                <Checkbox
                  size="small"
                  color="primary"
                  name="agreeTerms"
                  checked={values.agreeTerms}
                  onChange={(e) => setFieldValue('agreeTerms', e.target.checked)}
                  sx={{ p: 0, mr: 0.75, mt: '2px' }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6, fontSize: 13 }}>
                  By Signing up, you agree to our{' '}
                  <Link component={RouterLink} to="/terms-of-use" sx={{ color: '#059669', textDecoration: 'underline' }}>
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link component={RouterLink} to="/privacy-policy" sx={{ color: '#059669', textDecoration: 'underline' }}>
                    Privacy Policy
                  </Link>
                </Typography>
              }
            />
            {touched.agreeTerms && errors.agreeTerms && (
              <FormHelperText error sx={{ mb: 1 }}>{errors.agreeTerms as string}</FormHelperText>
            )}

            {errors.submit && (
              <FormHelperText error sx={{ mb: 1 }}>{errors.submit as string}</FormHelperText>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: '100%',
                background: '#059669',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '10px 16px',
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {isSubmitting ? 'Logging in...' : 'Login'}
            </button>

            <div style={{ margin: '12px 0 10px' }}>
              <Divider>
                <Typography variant="caption" sx={{ color: '#888' }}>Login with</Typography>
              </Divider>
            </div>

            <SocialIcons />
          </form>
        )}
      </Formik>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 13,
  fontWeight: 600,
  color: '#333',
  marginBottom: 6,
};

const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    background: '#fff',
    '& fieldset': { borderColor: '#d1d5db' },
    '&:hover fieldset': { borderColor: '#059669' },
    '&.Mui-focused fieldset': { borderColor: '#059669' },
  },
};

export default HeroLoginForm;
